import type { ReactNode } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, Stack, Title, Text, Group, Skeleton, Box, Button } from '@repletoryreach/sdk-ui';
import { Alert, Tabs, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { useUsers } from '../../../data/useUsers';
import { useTenants } from '../../../data/useTenants';
import { formatDate, formatRelative } from '../../../lib/format';
import { CopyableId } from '../../../components/CopyableId';
import { TenantName } from '../../../components/TenantName';
import { PersonAvatar } from '../components/PersonAvatar';
import { ChangeScopeButton } from './components/ChangeScopeButton';
import { AccountTab } from './components/AccountTab';
import { ActivityTab } from './components/ActivityTab';
import { SecurityTab } from './components/SecurityTab';
import { DangerTab } from './components/DangerTab';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
const TABS = ['account', 'activity', 'security', 'danger'] as const;

export default function UserViewPage() {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: usersData, isLoading, error, refetch, isFetching } = useUsers();
  const { data: tenantsData } = useTenants();

  const user = usersData?.data.find((u) => u.id === id);
  const tenant = tenantsData?.data.find((row) => row.id === user?.organisation_id);

  const requested = searchParams.get('tab');
  const activeTab = (TABS as readonly string[]).includes(requested ?? '') ? (requested as string) : 'account';
  function selectTab(next: string) {
    const params = new URLSearchParams(searchParams);
    if (next === 'account') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={20} width="40%" />
        <Card withBorder><Stack gap="md"><Skeleton height={28} width="45%" /><Skeleton height={16} width="60%" /><Skeleton height={16} width="35%" /></Stack></Card>
        <Skeleton height={40} />
        <Card withBorder><Stack gap="md"><Skeleton height={18} /><Skeleton height={18} /><Skeleton height={18} /></Stack></Card>
      </Stack>
    );
  }

  // A failed fetch is not the same answer as "no such user", and only one of
  // the two is worth retrying.
  if (error) {
    return (
      <Alert color="error">
        <Stack gap="sm">
          <span>{t('users.loadError')}</span>
          <Group gap="sm">
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.retry')}</Button>
          </Group>
        </Stack>
      </Alert>
    );
  }

  if (!user) {
    return (
      <Alert color="warning">
        <Stack gap="sm">
          <span>{t('users.view.notFound')}</span>
          <Group gap="sm">
            <Button size="xs" variant="outline" onClick={() => navigate('/users')}>{t('users.view.backToAll')}</Button>
          </Group>
        </Stack>
      </Alert>
    );
  }

  // `email` is nullable in the database though the SDK types it as a string,
  // so the person's name is what identifies them and email is the fallback.
  const heading = user.display_name || user.email || t('users.unnamed');

  return (
    <Stack gap="lg">
      {/* The shell header already carries Admin › Users › …, so the page does
          not draw a second breadcrumb beneath it. */}
      <Card withBorder>
        <Stack gap="md">
          <Link to="/users" style={{ color: MUTED, fontSize: 13, textDecoration: 'none', width: 'fit-content' }}>
            <span aria-hidden>←</span> {t('users.view.backToAll')}
          </Link>

          <Group justify="space-between" align="flex-start" gap="md" wrap="wrap">
            <Group gap="md" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
              <PersonAvatar name={heading} size="lg" />
              <Stack gap="xs" style={{ minWidth: 0 }}>
                <Group gap="sm" align="center" wrap="wrap">
                  <Title order={1} style={{ fontSize: 28, margin: 0, lineHeight: 1.15 }}>{heading}</Title>
                  <StatusBadge size="sm" status={user.scope === 'platform' ? 'warning' : 'pending'}>
                    {user.scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')}
                  </StatusBadge>
                </Group>
                <Text fz="sm" c={MUTED}>{user.email ?? t('users.noEmail')}</Text>
              </Stack>
            </Group>

            <Group gap="sm" align="center">
              <ChangeScopeButton userId={user.id} email={user.email ?? heading} current={user.scope} />
              {/* Platform staff belong to no customer organisation (3 October 2026). */}
              {user.scope !== 'platform' && (
                <Link
                  to={`/tenants/${user.organisation_id}`}
                  style={{ color: 'var(--rr-color-primary)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
                >
                  {t('users.view.openTenant')}
                </Link>
              )}
            </Group>
          </Group>

          {/* The facts support quotes back, label above value. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
              gap: 16,
              paddingTop: 16,
              borderTop: '1px solid var(--rr-color-border)'
            }}
          >
            <Meta label={t('users.filters.scope')} value={user.scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')} />
            <Meta label={t('common.tenant')} value={user.scope === 'platform' ? t('users.view.platformTeam') : <TenantName id={user.organisation_id} />} />
            <Meta
              label={t('common.created')}
              value={formatDate(user.created_at)}
              hint={formatRelative(user.created_at)}
            />
            <Meta
              label={t('users.view.account.userId')}
              value={<CopyableId value={user.id} label={t('users.view.copyUserId')} chars={12} />}
            />
          </div>
        </Stack>
      </Card>

      <Tabs value={activeTab} onChange={selectTab}>
        <Tabs.List>
          <Tabs.Tab value="account">{t('users.view.account.title')}</Tabs.Tab>
          <Tabs.Tab value="activity">{t('users.view.tabs.tenantActivity')}</Tabs.Tab>
          <Tabs.Tab value="security">{t('settings.layout.nav.security')}</Tabs.Tab>
          {/* The design colours this tab: it is the one that holds actions
              nothing else on the page can undo. */}
          <Tabs.Tab value="danger">
            <span style={{ color: 'var(--rr-color-error)' }}>{t('users.view.tabs.danger')}</span>
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="account">
          <Box py="lg"><AccountTab user={user} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="activity">
          <Box py="lg"><ActivityTab tenantId={user.organisation_id} tenantName={tenant?.name} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="security">
          <Box py="lg"><SecurityTab user={user} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="danger">
          <Box py="lg"><DangerTab user={user} /></Box>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function Meta({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <Text role="overline" c={MUTED}>{label}</Text>
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2, minWidth: 0, overflow: 'hidden' }}>{value}</div>
      {hint && <Text role="caption" c={MUTED}>{hint}</Text>}
    </div>
  );
}
